import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
	Box,
	IconButton,
	List,
	ListItemButton,
	Popover,
} from '@mui/material';
import PaletteIcon from '@mui/icons-material/Palette';
import { bookmarkColors, nextBookmarkColor } from '../constants/bookmarkColors';

export interface NoteColorPickerButtonProps {
	value: string | null;
	onChange: (color: string | null) => void;
	tabIndex?: number;
}

/** Palette button: each click cycles color; popover stays open for direct picks. */
export function NoteColorPickerButton({
	value,
	onChange,
	tabIndex = -1,
}: NoteColorPickerButtonProps) {
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const paperRef = useRef<HTMLDivElement>(null);
	const open = Boolean(anchorEl);

	const handleClose = useCallback(() => {
		setAnchorEl(null);
	}, []);

	useEffect(() => {
		if (!open) {
			return;
		}
		const onPointerDown = (event: PointerEvent) => {
			const target = event.target as Node;
			if (anchorEl?.contains(target)) {
				return;
			}
			if (paperRef.current?.contains(target)) {
				return;
			}
			handleClose();
		};
		document.addEventListener('pointerdown', onPointerDown, true);
		return () => document.removeEventListener('pointerdown', onPointerDown, true);
	}, [open, anchorEl, handleClose]);

	const handleButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		event.preventDefault();
		event.stopPropagation();
		setAnchorEl(event.currentTarget);
		onChange(nextBookmarkColor(value));
	};

	const handlePick = (next: string | null) => {
		onChange(next);
	};

	return (
		<>
			<IconButton
				type='button'
				onClick={handleButtonClick}
				aria-label='Note color'
				aria-expanded={open ? 'true' : undefined}
				aria-haspopup='true'
				title={value ? `Note color ${value}` : 'No note color — click to cycle'}
				tabIndex={tabIndex}
				sx={{ '&:hover': { opacity: 0.9 } }}
			>
				<PaletteIcon fontSize='small' sx={{ color: value ? value : 'action.disabled' }} />
			</IconButton>
			<Popover
				open={open}
				anchorEl={anchorEl}
				onClose={(_, reason) => {
					if (reason === 'backdropClick') {
						return;
					}
					handleClose();
				}}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'center',
				}}
				transformOrigin={{
					vertical: 'top',
					horizontal: 'center',
				}}
				disableRestoreFocus
				slotProps={{
					root: {
						sx: { pointerEvents: 'none' },
					},
					paper: {
						ref: paperRef,
						sx: {
							pointerEvents: 'auto',
							py: 0.25,
							px: 0.25,
							minWidth: 40,
						},
					},
				}}
			>
				<List dense disablePadding>
					{bookmarkColors.map((hex) => (
						<ListItemButton
							key={hex}
							selected={value === hex}
							aria-label={`Set note color ${hex}`}
							onClick={() => handlePick(hex)}
							sx={{ py: 0.25, px: 0.5, minHeight: 0 }}
						>
							<Box
								sx={{
									width: 28,
									height: 10,
									borderRadius: 0.5,
									bgcolor: hex,
									border: '1px solid',
									borderColor: value === hex ? 'primary.main' : 'divider',
								}}
							/>
						</ListItemButton>
					))}
					<ListItemButton
						selected={value == null}
						aria-label='No note color'
						onClick={() => handlePick(null)}
						sx={{ py: 0.25, px: 0.5, minHeight: 0 }}
					>
						<Box
							sx={{
								width: 28,
								height: 10,
								borderRadius: 0.5,
								border: '1px dashed',
								borderColor: value == null ? 'primary.main' : 'divider',
							}}
						/>
					</ListItemButton>
				</List>
			</Popover>
		</>
	);
}

export default NoteColorPickerButton;
